import { Form, FormControl } from "react-bootstrap";
import { useFormikContext } from "formik";

import { AddFareZone } from "../../types/AddFareZone";
import { EditFareZone } from "../../types/EditFareZone";
import { AddFare } from "../../types/AddFare";

import CurrencyFormControl from "../../@ui/CurrencyFormControl";
import { ErrorMessage } from "../../@ui/ErrorMessage";

export interface FareEditorProps {
  minEffectiveFrom: Date;
}

const FareEditor = ({ minEffectiveFrom }: FareEditorProps) => {
  const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
    useFormikContext<AddFareZone | EditFareZone>();

  const calculateFares = (e: any) => {
    if (!values.fare) return;
    const adultDailyFare = e.target.value;

    // 50% discount
    const childDailyFare = roundUpToNearest5Pence(adultDailyFare * 0.5);
    // 34% discount
    const railcardDailyFare = roundDownToNearest5Pence(adultDailyFare * 0.666);
    setFieldValue("fare.childDailyFare", childDailyFare);
    setFieldValue("fare.railcardDailyFare", railcardDailyFare);
  };

  const roundDownToNearest5Pence = (value: number) => {
    return Math.floor(value / 0.05) * 0.05;
  };

  const roundUpToNearest5Pence = (value: number) => {
    return Math.ceil(value / 0.05) * 0.05;
  };

  const toYYYYMMDD = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}-${month <= 9 ? "0" + month : month}-${
      day <= 9 ? "0" + day : day
    }`;
  };

  return (
    <>
      {values.fare && (
        <>
          <Form.Group>
            <Form.Label htmlFor="fare.effectiveFrom">Effective from</Form.Label>
            {(errors.fare as unknown as AddFare)?.effectiveFrom &&
              (touched.fare as unknown as AddFare)?.effectiveFrom && (
                <ErrorMessage>
                  {(errors.fare as unknown as AddFare)?.effectiveFrom as string}
                </ErrorMessage>
              )}
            <FormControl
              type="date"
              name="fare.effectiveFrom"
              id="fare.effectiveFrom"
              value={values.fare.effectiveFrom.substring(0, 10)}
              onChange={handleChange}
              onBlur={handleBlur}
              style={{ width: 150 }}
              min={toYYYYMMDD(minEffectiveFrom)}
            />
          </Form.Group>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th></th>
                <th>Daily</th>
                <th>Weekly</th>
                <th>Monthly</th>
                <th>Quarterly</th>
                <th>Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr className="align-middle">
                <td>Adult</td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.adultDailyFare"
                    id="fare.adultDailyFare"
                    value={values.fare.adultDailyFare}
                    onChange={(e: any) => calculateFares(e)}
                  ></CurrencyFormControl>
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.adultWeeklyFare"
                    id="fare.adultWeeklyFare"
                    value={values.fare.adultWeeklyFare ?? ""}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.adultMonthlyFare"
                    id="fare.adultMonthlyFare"
                    value={values.fare.adultMonthlyFare ?? ""}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.adultQuarterlyFare"
                    id="fare.adultQuarterlyFare"
                    value={values.fare.adultQuarterlyFare ?? ""}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.adultAnnualFare"
                    id="fare.adultAnnualFare"
                    value={values.fare.adultAnnualFare ?? ""}
                  />
                </td>
              </tr>
              <tr className="align-middle">
                <td>Child</td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.childDailyFare"
                    id="fare.childDailyFare"
                    value={values.fare.childDailyFare}
                    disabled={true}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.childWeeklyFare"
                    id="fare.childWeeklyFare"
                    value={values.fare.childWeeklyFare}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.childMonthlyFare"
                    id="fare.childMonthlyFare"
                    value={values.fare.childMonthlyFare}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.childQuarterlyFare"
                    id="fare.childQuarterlyFare"
                    value={values.fare.childQuarterlyFare}
                  />
                </td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.childAnnualFare"
                    id="fare.childAnnualFare"
                    value={values.fare.childAnnualFare}
                  />
                </td>
              </tr>
              <tr className="align-middle">
                <td>Rail Card</td>
                <td className="text-center">
                  <CurrencyFormControl
                    name="fare.railcardDailyFare"
                    id="fare.railcardDailyFare"
                    value={values.fare.railcardDailyFare}
                    disabled={true}
                  />
                </td>
                <td className="text-center">-</td>
                <td className="text-center">-</td>
                <td className="text-center">-</td>
                <td className="text-center">-</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

export default FareEditor;
